"use client";

import {
  PortfolioItem,
  PortfolioItemData,
} from "@/components/molecules/portfolioItem";
import { Box, chakra, Heading, IconButton } from "@chakra-ui/react";
import { useRef } from "react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import DOMPurify from "isomorphic-dompurify";

export interface PortfolioHighlightsProps {
  title: string;
  items: PortfolioItemData[];
}

export default chakra(PortfolioHighlights);
function PortfolioHighlights({ title, items }: PortfolioHighlightsProps) {
  const sliderRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!sliderRef.current) return;

    const scrollAmount = sliderRef.current.clientWidth * 0.8;

    sliderRef.current.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <Box
      as="section"
      display="grid"
      gap={{ base: 4, md: 8 }}
      gridTemplateColumns={{ base: "1fr", md: "1fr auto" }}
    >
      <Box gridColumn="1" px={{ base: 4, md: 10 }}>
        {title && (
          <Heading
            as="h2"
            fontSize={{ base: "2xl", md: "2rem", lg: "5xl" }}
            fontWeight={400}
            color="brand.aubergine"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(title.replace(/<\/p>/g, "</br>"), {
                ALLOWED_TAGS: ["strong", "br"],
              }),
            }}
          />
        )}
      </Box>
      <Box
        gridColumn={{ base: "1", md: "2" }}
        gridRow={{ base: "3", md: "1" }}
        display="flex"
        gap={2}
        px={{ base: 4, md: 10 }}
        justifyContent={{ base: "flex-start", md: "flex-end" }}
      >
        <IconButton
          aria-label="Vorige"
          icon={<FiArrowLeft />}
          onClick={() => scroll("left")}
          color="brand.white"
          bgColor="brand.aubergine"
          _hover={{ bgColor: "brand.aubergine" }}
          _active={{ bgColor: "brand.aubergine" }}
        />

        <IconButton
          aria-label="Volgende"
          icon={<FiArrowRight />}
          onClick={() => scroll("right")}
          color="brand.white"
          bgColor="brand.aubergine"
          _hover={{ bgColor: "brand.aubergine" }}
          _active={{ bgColor: "brand.aubergine" }}
        />
      </Box>
      <Box
        ref={sliderRef}
        gridColumn="1 / -1"
        gridRow="2"
        overflowX="auto"
        scrollBehavior="smooth"
        scrollSnapType="x mandatory"
        scrollPaddingX={{ base: 4, md: 10 }}
        sx={{ scrollbarWidth: "none" }}
      >
        <Box display="flex" px={{ base: 4, md: 10 }}>
          {items.map((item, index) => (
            //--index as key is accepted, list is static--//
            <Box
              key={index}
              scrollSnapAlign="start"
              flex={{
                base: "0 0 85%",
                md: "0 0 45%",
                lg: "0 0 22%",
              }}
              px={4}
              borderRight="1px solid"
              borderColor="gray.200"
              _first={{
                pl: 0,
              }}
              _last={{
                pr: { base: 4, md: 10 },
                borderRight: "none",
              }}
            >
              <PortfolioItem item={item} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
